/**
 * Authenticator allowlist feature — schemas.
 *
 * Tenant-scoped. The allowlist pins which authenticator models
 * (by AAGUID) are accepted during attestation-enforced WebAuthn
 * registration. Rows arrive three ways: manually (source="manual"),
 * from the FIDO MDS sync worker (source="mds"), or shipped by PureID
 * as a removable default (source="vendor_default" — today the PureID
 * software authenticator, pre-baked so a tenant's first Windows logon
 * registration is not refused at a lock screen). The UI distinguishes
 * all three so an operator can see who put each row there.
 *
 * A vendor default is an ORDINARY ROW on every surface: it lists, it
 * deletes through the same endpoint, and removing it is a supported
 * opt-out that nothing re-seeds. Do not add a code path that hides it
 * or blocks its removal.
 */

import { z } from "zod";

export const knownAllowlistSourceSchema = z.enum([
  "manual",
  "mds",
  "vendor_default",
]);
export type KnownAllowlistSource = z.infer<typeof knownAllowlistSourceSchema>;

// Deliberately widened past the known set. `source` is a server-owned
// vocabulary behind a CHECK constraint the backend can extend in a
// migration, and the migration is a SEPARATE gated deploy step from the
// console bundle. A strict enum here turns that ordering window into
// "Failed to load allowlist" on the whole page — the list is the
// operator's only view of what their tenant admits, so it must degrade
// to an unfamiliar label, never to nothing. sourceLabel() handles the
// unknown case.
export const allowlistSourceSchema = z.union([
  knownAllowlistSourceSchema,
  z.string(),
]);
export type AllowlistSource = z.infer<typeof allowlistSourceSchema>;

export const allowlistEntrySchema = z.object({
  id: z.string(),
  aaguid: z.string(),
  display_name: z.string(),
  vendor: z.string().nullable().optional(),
  source: allowlistSourceSchema,
  notes: z.string().nullable().optional(),
  added_by: z.string().optional(),
  added_at: z.string(),
  // OPTIONAL HERE, AND NOT BECAUSE THE SERVER MAY OMIT IT WHEN FALSE.
  //
  // The backend serialises this field with NO `omitempty`, deliberately, so a
  // `false` always appears on the wire (see AllowlistEntryResponse in
  // backend/pkg/api/tenant/authnallowlist.go). Within a server that knows the
  // field, present-and-false is a real answer: "this model is specifically NOT
  // approved for roaming logon."
  //
  // It is optional in the SCHEMA only to cover the one case the server cannot
  // signal any other way: a backend older than migration 000072, which has no
  // such column and emits no such key. `.optional()` makes that a THIRD state —
  // `undefined` — instead of collapsing it into `false` via a `.catch(false)` or
  // a `.default(false)`, either of which would have this console assert "not
  // allowed" about a server that never had an opinion. Do not add a default
  // here; roamingGrant() below is what turns the three states into words.
  roaming_logon_allowed: z.boolean().optional(),
});
export type AllowlistEntry = z.infer<typeof allowlistEntrySchema>;

/**
 * The three states of the roaming-logon grant, kept distinct all the way to
 * the pixel.
 *
 * "unknown" is NOT a soft "no". It means the server did not report the field,
 * which today means only one thing — a backend that predates the grant — and
 * an operator reading it needs to know their answer is coming from somewhere
 * older than the question. Rendering it as "not allowed" would be a lie that
 * happens to be operationally right, which is the worst kind on a security
 * surface: it reads as a decision somebody made.
 */
export type RoamingGrant = "allowed" | "not_allowed" | "unknown";

export function roamingGrant(entry: AllowlistEntry): RoamingGrant {
  if (entry.roaming_logon_allowed === undefined) return "unknown";
  return entry.roaming_logon_allowed ? "allowed" : "not_allowed";
}

/**
 * Provenance label for the row's badge — who put this entry on the list.
 *
 * "PureID default" is the honest rendering of source="vendor_default":
 * it says we shipped it, which "Manual" would not. No status or roadmap
 * vocabulary and no internal jargon (the column is provenance, never
 * "seed" or "bypass").
 *
 * An unrecognised value falls through to the raw string rather than a
 * guess, so a console bundle older than the backend's vocabulary is
 * merely uninformative instead of wrong.
 */
export function sourceLabel(source: AllowlistSource): string {
  switch (source) {
    case "mds":
      return "FIDO MDS";
    case "manual":
      return "Manual";
    case "vendor_default":
      return "PureID default";
    default:
      return source;
  }
}

export const listAllowlistResponseSchema = z.object({
  items: z.array(allowlistEntrySchema),
});
export type ListAllowlistResponse = z.infer<typeof listAllowlistResponseSchema>;

/**
 * Whether the tenant's WebAuthn attestation preference makes passkey
 * registration consult this allowlist. The ceremony enforces it only under
 * `direct` or `enterprise` (RegistrationFinish in
 * backend/internal/identity/ceremony/service.go). Under `none` or `indirect`
 * any authenticator registers, whatever the list holds. Every console surface
 * that says whether enforcement is on derives it here; an unknown value is
 * not enforcing, which is what the ceremony does with it.
 */
export function attestationEnforcesAllowlist(attestation: string): boolean {
  return attestation === "direct" || attestation === "enterprise";
}

// ── Approved models list: URL state ────────────────────────────────────────
// ?q=&source=&roaming=&sort=&page= on Authenticators › Approved models.
// Every field is optional (the clean URL is the default view) and each one
// falls back to undefined rather than failing the route on a hand-edited or
// stale URL. TanStack parses search values as JSON, so `?q=1234` arrives as a
// number: q accepts both and is kept as text.

export const modelSourceFilterSchema = knownAllowlistSourceSchema;
export type ModelSourceFilter = z.infer<typeof modelSourceFilterSchema>;

export const approvedModelsSearchSchema = z.object({
  q: z
    .union([z.string(), z.number()])
    .transform((v) => String(v))
    .pipe(z.string().max(200))
    .optional()
    .catch(undefined),
  source: modelSourceFilterSchema.optional().catch(undefined),
  roaming: z.literal(true).optional().catch(undefined),
  sort: z.literal("recent").optional().catch(undefined),
  page: z.number().int().min(1).max(10_000).optional().catch(undefined),
});
export type ApprovedModelsSearch = z.infer<typeof approvedModelsSearchSchema>;

// Matches the backend CreateAllowlistEntryRequest. AAGUID is
// validated here as a UUID so the server doesn't have to
// reject malformed input — a clean client-side error is a
// better UX. Display name is 1..255 chars to match the
// schema CHECK.
// An AAGUID is any 16-byte identifier written 8-4-4-4-12 in hex. It is NOT
// required to be an RFC 9562 UUID: real models carry version and variant
// nibbles a strict UUID check refuses (Samsung Pass 53414d53-554e-4700-0000-…,
// Chrome on Mac adce0002-35bc-c60a-…), and the server parses it with
// uuid.Parse, which accepts any hex in this shape.
export const AAGUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const createAllowlistEntrySchema = z.object({
  aaguid: z
    .string()
    .trim()
    .regex(AAGUID_PATTERN, "Enter all 36 characters, like ee882879-721c-4913-8966-c3ea9ec99f9a."),
  display_name: z
    .string()
    .trim()
    .min(1, "Enter a name for the list and audit events.")
    .max(255, "Display name must be 255 characters or fewer"),
  vendor: z.string().max(255).optional(),
  notes: z.string().max(1000).optional(),
  // The second grant. Required in the INPUT type (no `.optional()`, no
  // `.default()` — a default inside an RHF resolver widens the input type to
  // allow `undefined` and breaks SubmitHandler), so the form must state a
  // value rather than inherit one. `defaultValues` supplies the `false`.
  //
  // WHY IT MUST BE ITS OWN DECISION. Putting a row on this list already grants
  // the model two things: platform Windows logon, and frontline tap-and-go.
  // This is the separate, narrower question of whether a credential on a key
  // the user CARRIES may hold an Active Directory logon certificate. If row
  // presence had implied it, the healthcare hardware-NFC preset — whose whole
  // job is to bulk-add carried NFC keys — would have handed a domain logon
  // certificate to every model in the catalogue, and nothing on that screen
  // would have said so.
  roaming_logon_allowed: z.boolean(),
});
export type CreateAllowlistEntryInput = z.infer<
  typeof createAllowlistEntrySchema
>;

/**
 * Add a model: the wire schema plus the one check that needs the loaded list.
 * An AAGUID already on the list is refused at the form (the server would 409)
 * and names the row that holds it. A display name already in use is only a
 * warning — names can't be changed later, so the page says so — and is not
 * part of validation.
 */
export function addModelFormSchema(existing: readonly AllowlistEntry[]) {
  return createAllowlistEntrySchema.superRefine((data, ctx) => {
    const wanted = data.aaguid.trim().toLowerCase();
    const dup = existing.find((e) => e.aaguid.trim().toLowerCase() === wanted);
    if (dup) {
      ctx.addIssue({ code: "custom", path: ["aaguid"], message: `Already on the list as ${dup.display_name}.` });
    }
  });
}
